// Review step (stories 58-63, round-1 decision 17): everything the sizing rests on as one compact
// table, so the SE can confirm it with the customer in a single pass.
//
// Component tree
//   ReviewStep
//   ├─ re-check flags that belong to no row (dismissable)
//   ├─ <table>   columns: Metric | Value (inline editable) | Status (Assumed / Collected / Re-check)
//   │    ├─ DealGroup          ACV, win rate, current annual pipeline when a use case needs it
//   │    └─ UseCaseGroup       header row (name, tier, Clay-attributed value), overlap notes, one row
//   │                          per input question (Tool consolidation: one per selected tool), then the
//   │                          realization and attribution factor rows (tangerine left strip)
//   └─ (the footer's "View ROI summary" is the only way on)
//
// What the table does not show: the Clay default beside a value (a blank, Assumed row greys the
// default into the input's placeholder), the summary-counts strip, metric history and the
// record-level / selection links (those live on the selection step and the use case page).
//
// Editing goes through `useUseCaseEditor` and `useDealDraft`: the same re-render-safe drafts
// (seeded once per store `epoch`) the use case and deal pages use, so a value edited here is the
// value on those pages. Each keystroke also pushes into the store, which clears a matching re-check
// flag, so a Re-check status disappears on edit.

import type { ReactNode } from 'react';
import { useDraftState } from '../../../_shared/hooks';
import { AUTOFILL_IGNORE_PROPS } from '../../../_shared/autofill';
import { numberToInput, parseNumberInput } from '../../flow/number-input';
import { NumberField } from './NumberField';
import { calculatorActions, sizingOf, useCalculatorStore } from '../../state/calculator-store';
import { useShallow } from 'zustand/react/shallow';
import { DEFAULT_TOOLS16 } from '../../data/tools';
import type { CustomTool } from '../../types';
import { Ph } from '../Icon';
import { useDealDraft } from './DealEconomicsStep';
import {
  REVIEW_STATUS_LABEL,
  buildReviewTable,
  type RecheckVm,
  type ReviewStatus,
  type ReviewTableGroup,
  type ReviewTableRow,
} from './review-model';
import { useUseCaseEditor } from './UseCasePage';

/* ----------------------------------------------------------------------- re-check */

function RecheckList({ items }: { items: RecheckVm[] }) {
  if (items.length === 0) return null;
  return (
    <ul className="vc3-rechecks" aria-label="Answers to re-check">
      {items.map((r) => (
        <li key={`${r.flag.useCaseId ?? 'deal'}|${r.flag.key}|${r.flag.from ?? ''}`} className="vc3-recheck-item">
          <span className="vc3-recheck">Re-check</span>
          <span className="vc3-recheck-text">
            <strong>{r.kindLabel}.</strong> {r.reason}
            {r.was && <> It was {r.was} in the earlier version.</>}
          </span>
          <button type="button" className="vc3-linkbtn" onClick={() => calculatorActions.dismissRecheck(r.flag)}>
            Dismiss
          </button>
        </li>
      ))}
    </ul>
  );
}

/* ---------------------------------------------------------------------- table cells */

function StatusChip({ status, reason }: { status: ReviewStatus; reason: string | null }) {
  return (
    <span className="vc3-rt-status" data-status={status} title={reason ?? undefined}>
      {REVIEW_STATUS_LABEL[status]}
    </span>
  );
}

function ValueInput({
  id,
  label,
  unit,
  value,
  placeholder,
  invalid,
  onChange,
}: {
  id: string;
  label: string;
  unit: ReviewTableRow['unit'];
  value: string;
  placeholder?: string;
  invalid?: boolean;
  onChange: (text: string) => void;
}) {
  return (
    <span className="vc3-rt-val" data-unit={unit}>
      {unit === '$' && <span aria-hidden>$</span>}
      <NumberField
        id={id}
        className="vc3-rt-input"
        {...AUTOFILL_IGNORE_PROPS}
        aria-label={label}
        aria-invalid={invalid}
        value={value}
        placeholder={placeholder}
        onChange={onChange}
      />
      {unit !== '' && unit !== '$' && <span aria-hidden>{unit}</span>}
    </span>
  );
}

function Row({ row, children }: { row: ReviewTableRow; children: ReactNode }) {
  return (
    <tr
      className="vc3-rt-row"
      data-status={row.status}
      data-factor={row.source.type === 'factor' ? row.source.which : undefined}
    >
      <th scope="row" className="vc3-rt-metric">
        {row.label}
        {row.source.type === 'factor' && <span className="vc3-rt-factorchip">Factor</span>}
      </th>
      <td className="vc3-rt-value">{children}</td>
      <td className="vc3-rt-statuscell">
        <StatusChip status={row.status} reason={row.reason} />
      </td>
    </tr>
  );
}

function FlagRow({ items }: { items: RecheckVm[] }) {
  if (items.length === 0) return null;
  return (
    <tr className="vc3-rt-note">
      <td colSpan={3}>
        <RecheckList items={items} />
      </td>
    </tr>
  );
}

function GroupHead({ group }: { group: ReviewTableGroup }) {
  return (
    <>
      <tr className="vc3-rt-head">
        <th colSpan={3} scope="colgroup">
          <span className="vc3-rt-head-in">
            {group.tier && (
              <span className="vc3-tierbadge" data-tier={group.tier}>
                {group.tier}
              </span>
            )}
            <span className="vc3-rt-name">{group.name}</span>
            {group.attributedText && (
              <span className="vc3-rt-attr">
                <span className="vc3-rt-attr-fig" data-tone="program">
                  <strong>{group.programText}</strong> Program value
                </span>
                <span className="vc3-rt-attr-fig" data-tone="attributed">
                  <strong>{group.attributedText}</strong> {group.attributedLabel}
                </span>
              </span>
            )}
          </span>
        </th>
      </tr>
      {group.notes.map((n) => (
        <tr key={n} className="vc3-rt-note">
          <td colSpan={3}>
            <span className="vc3-rt-overlap" role="note">
              <Ph name="info" size={14} style={{ flexShrink: 0, marginTop: 2 }} /> {n}
            </span>
          </td>
        </tr>
      ))}
    </>
  );
}

/**
 * A selected tool's annual contract value. The text is a draft seeded once per store epoch; each
 * keystroke also pushes the parsed number to the store. Blank on a built-in vendor restores that
 * vendor's default (so Assumed always means "still the default"); no adding, removing or ticking here.
 */
function ToolValue({ row }: { row: ReviewTableRow }) {
  const src = row.source;
  const epoch = useCalculatorStore((s) => s.epoch);
  const spend = useCalculatorStore((s) => {
    if (src.type !== 'tool') return null;
    if (src.toolId) return (s.tools ?? DEFAULT_TOOLS16)[src.toolId] ?? null;
    return s.customTools?.[src.index ?? -1]?.spend ?? null;
  });
  const seed = { text: numberToInput(spend === '' || spend == null ? null : Number(spend)) };
  const { draft, setDraft } = useDraftState<{ text: string }>(seed, `${epoch}:${row.id}`);
  if (src.type !== 'tool') return null;
  const onChange = (text: string) => {
    setDraft({ text });
    const n = parseNumberInput(text);
    if (src.toolId) {
      if (text.trim() === '') calculatorActions.setToolSpend(src.toolId, src.defaultSpend ?? '');
      else if (n !== null) calculatorActions.setToolSpend(src.toolId, n);
    } else {
      const next: CustomTool[] = (useCalculatorStore.getState().customTools ?? []).map((c, i) =>
        i === src.index ? { ...c, spend: n ?? '' } : c,
      );
      calculatorActions.setCustomTools(next);
    }
  };
  return (
    <ValueInput
      id={`vc3-rv-${row.id}`}
      label={`${row.label} annual contract value`}
      unit="$"
      value={(draft ?? seed).text}
      placeholder={src.defaultSpend != null ? String(src.defaultSpend) : undefined}
      onChange={onChange}
    />
  );
}

/* --------------------------------------------------------------------------- groups */

function DealGroup({ group }: { group: ReviewTableGroup }) {
  const { d, onChange } = useDealDraft();
  return (
    <tbody className="vc3-rt-group" data-group="deal">
      <GroupHead group={group} />
      <FlagRow items={group.looseFlags} />
      {group.rows.map((row) => {
        if (row.source.type !== 'deal') return null;
        const field = row.source.field;
        const invalid = field === 'winRate' && d.winRate !== '' && (parseNumberInput(d.winRate) ?? 101) > 100;
        return (
          <Row key={row.id} row={row}>
            <ValueInput
              id={`vc3-rv-${field}`}
              label={row.label}
              unit={row.unit}
              value={d[field]}
              invalid={invalid}
              onChange={(text) => onChange(field, text)}
            />
          </Row>
        );
      })}
    </tbody>
  );
}

function UseCaseGroup({ group }: { group: ReviewTableGroup }) {
  const e = useUseCaseEditor(group.id);
  if (!e) return null;
  const { uc, editing } = e;
  const questions = new Map(e.groups.flatMap((g) => g.questions).map((q) => [q.key, q]));

  return (
    <tbody className="vc3-rt-group" data-group={uc.id}>
      <GroupHead group={group} />
      <FlagRow items={group.looseFlags} />
      {group.rows.map((row) => {
        const src = row.source;
        if (src.type === 'answer') {
          const q = questions.get(src.key);
          if (!q) return null;
          return (
            <Row key={row.id} row={row}>
              <ValueInput
                id={`vc3-q-${uc.id}-${q.key}`}
                label={row.label}
                unit={row.unit}
                value={editing.answers.texts[q.key] ?? ''}
                placeholder={q.defText.replace(/^\$|%$| hrs$| min$/g, '')}
                invalid={
                  (editing.answers.texts[q.key] ?? '').trim() !== '' && parseNumberInput(editing.answers.texts[q.key] ?? '') === null
                }
                onChange={(text) => editing.answers.onChange(q, text)}
              />
            </Row>
          );
        }
        if (src.type === 'factor') {
          const factor = src.which === 'realization' ? editing.realization : editing.attribution;
          const def = src.which === 'realization' ? uc.realization.pct : uc.attribution.pct;
          return (
            <Row key={row.id} row={row}>
              <ValueInput
                id={`vc3-rv-${src.which === 'realization' ? 'rz' : 'at'}-${uc.id}`}
                label={row.label}
                unit="%"
                value={factor.text}
                placeholder={String(def)}
                onChange={factor.onChange}
              />
            </Row>
          );
        }
        if (src.type === 'tool') {
          return (
            <Row key={row.id} row={row}>
              <ToolValue row={row} />
            </Row>
          );
        }
        return null;
      })}
    </tbody>
  );
}

/* ------------------------------------------------------------------------- the step */

export function ReviewStep() {
  const sizing = useCalculatorStore(useShallow(sizingOf));
  const flags = useCalculatorStore((s) => s.recheck);
  const groups = buildReviewTable(sizing, flags);

  return (
    <section aria-labelledby="vc3-review-h">
      <div className="vc3-eyebrow">Review</div>
      <h1 id="vc3-review-h" className="vc3-h1">
        Review every input
      </h1>
      <p className="vc3-lede">
        Walk through the list. Anything marked Assumed is still Clay’s default, so check it against
        your own numbers before you present. Edit anything here; changes save as you go.
      </p>

      <div className="vc3-rt-wrap">
        <table className="vc3-rt">
          <thead>
            <tr>
              <th scope="col">Metric</th>
              <th scope="col">Value</th>
              <th scope="col">Status</th>
            </tr>
          </thead>
          {groups.map((g) => (g.kind === 'deal' ? <DealGroup key={g.id} group={g} /> : <UseCaseGroup key={g.id} group={g} />))}
        </table>
      </div>
    </section>
  );
}
